// The economy widgets (spec/dashboard.md section 7.4): the price sentence (a Hard / Soft / Ads segmented control, a chip
// with a menu in a grid cell; a switch clears what no longer applies and switching back restores it while the widget is
// open), the reward list (item × count), and reward-table drops with their chance.
import { useMemo, useRef, useState } from 'react';
import type { Json, Parameter } from '../../api/types';
import { Button, IconButton } from '../../ui/Button';
import { Banknote, Coins, Plus, Tv, X } from '../../ui/icons';
import { Checkbox, NumberInput } from '../../ui/inputs';
import { Menu } from '../../ui/Popover';
import { Tabs } from '../../ui/Tabs';
import { useContent } from '../ContentContext';
import { FieldWidget } from './FieldWidget';
import { joinPath, type WidgetProps } from './context';

export const PRICE_TEMPLATE = 'SmartObjects.Price';
export const REWARD_TEMPLATE = 'SmartObjects.Reward';
export const ITEM_AMOUNT_TEMPLATE = 'SmartObjects.ItemWithAmount';
export const REWARD_DROP_TEMPLATE = 'LiveOps.RewardDrop';

type Obj = Record<string, Json>;

function obj(value: Json | undefined): Obj {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Obj) : {};
}

/** `item × count` rows over a list of ItemWithAmount objects. */
export function ItemAmountList({ value, onCommit, onCommitAt, disabled, owner, label }: { value: Json | undefined; onCommit: (v: Json) => void; onCommitAt?: (rel: string, v: Json) => void; disabled?: boolean; owner?: string; label: string }) {
  const catalog = useContent();
  const t = catalog.templates.get(ITEM_AMOUNT_TEMPLATE);
  const itemParam = t?.effectiveParameters.find((p) => p.name === 'item');
  const rows = useMemo(() => (Array.isArray(value) ? (value as Json[]) : []), [value]);
  const set = (i: number, field: string, v: Json) => {
    if (onCommitAt) onCommitAt(`[${i}].${field}`, v);
    else onCommit(rows.map((r, j) => (j === i ? { ...obj(r), [field]: v } : r)));
  };
  return (
    <div className="item-amounts" data-testid={`item-amounts-${label}`}>
      {rows.map((r, i) => {
        const row = obj(r);
        return (
          <div key={i} className="row item-amount-row">
            {itemParam ? <FieldWidget param={{ ...itemParam, displayName: `${label} item ${i + 1}` }} value={row.item} compact disabled={disabled} owner={owner} onCommit={(v) => set(i, 'item', v)} /> : <span className="error small">no ItemWithAmount template</span>}
            <span className="muted" aria-hidden="true">×</span>
            <NumberInput size="s" kind="int" value={typeof row.count === 'number' ? row.count : 0} disabled={disabled} aria-label={`${label} count ${i + 1}`} onCommit={(v) => set(i, 'count', Number(v ?? 0))} />
            {!disabled && (
              <IconButton label={`Remove ${label} item ${i + 1}`} onClick={() => onCommit(rows.filter((_, j) => j !== i))}>
                <X aria-hidden="true" />
              </IconButton>
            )}
          </div>
        );
      })}
      {rows.length === 0 && <span className="muted small">No items yet.</span>}
      {!disabled && (
        <Button size="s" onClick={() => onCommit([...rows, { item: null, count: 1 }])} icon={<Plus aria-hidden="true" />}>
          Add item
        </Button>
      )}
    </div>
  );
}

/** `SmartObjects.Reward`: the items list. */
export function RewardField({ param: p, value, onCommit, onCommitAt, disabled, owner, compact }: WidgetProps) {
  const reward = obj(value);
  if (compact) return <span className="muted small">{Array.isArray(reward.items) ? `${reward.items.length} item${reward.items.length === 1 ? '' : 's'}` : 'empty'}</span>;
  return <ItemAmountList label={p.displayName ?? p.name} value={reward.items} disabled={disabled} owner={owner} onCommit={(items) => onCommit({ ...reward, items })} onCommitAt={onCommitAt ? (rel, v) => onCommitAt(joinPath('items', rel), v) : undefined} />;
}

const PRICE_TYPES = ['Hard', 'Soft', 'Ads'];

function priceIcon(type: number) {
  return type === 0 ? <Banknote className="icon-s" aria-hidden="true" /> : type === 1 ? <Coins className="icon-s" aria-hidden="true" /> : <Tv className="icon-s" aria-hidden="true" />;
}

/** The fields one price type owns (what a switch away clears and a switch back restores). */
function fieldsOf(price: Obj, type: number): Obj {
  if (type === 0) return { product: price.product ?? null, isVirtual: Boolean(price.isVirtual) };
  if (type === 1) return { items: price.items ?? [] };
  return { ads: price.ads ?? 0 };
}

/**
 * `SmartObjects.Price` (section 7.4 PriceField): a Hard / Soft / Ads segmented control (a chip with a menu in a grid
 * cell) and the sentence of the chosen type. A switch is one commit and writes the fields that no longer apply as empty
 * (research/02 quirk 10, decision 3); the widget keeps the previous type's values while it stays mounted, so switching
 * back restores them instead of starting over.
 */
export function PriceField({ param: p, value, onCommit, onCommitAt, disabled, owner, compact }: WidgetProps) {
  const catalog = useContent();
  const t = catalog.templates.get(PRICE_TEMPLATE);
  const productParam = t?.effectiveParameters.find((x) => x.name === 'product');
  const stash = useRef<Record<number, Obj>>({});
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const price = obj(value);
  const type = Number(price.type ?? 0);
  const label = p.displayName ?? p.name;
  const summary = type === 0 ? (price.product ? catalog.format.productLabel?.((price.product as { productId?: string }).productId ?? '') ?? 'product' : 'Free') : type === 1 ? `${Array.isArray(price.items) ? price.items.length : 0} item${Array.isArray(price.items) && price.items.length === 1 ? '' : 's'}` : `${String(price.ads ?? 0)} ad${Number(price.ads ?? 0) === 1 ? '' : 's'}`;
  const switchTo = (next: number) => {
    if (next === type) return;
    stash.current[type] = fieldsOf(price, type);
    const back = stash.current[next] ?? fieldsOf(price, next);
    const switched: Obj = {
      ...price,
      type: next,
      product: next === 0 ? (back.product ?? null) : null,
      isVirtual: next === 0 ? Boolean(back.isVirtual) : false,
      items: next === 1 ? (back.items ?? []) : [],
      ads: next === 2 ? Number(back.ads ?? 0) || 1 : 0,
    };
    onCommit(switched);
  };
  if (compact) {
    if (disabled) return <span>{PRICE_TYPES[type] ?? '?'}: {summary}</span>;
    return (
      <span className="row price-compact">
        <button type="button" className="card-chip price-type" aria-haspopup="menu" aria-expanded={menuAnchor !== null} onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label={`${label} type: ${PRICE_TYPES[type] ?? '?'}`}>
          {priceIcon(type)} {PRICE_TYPES[type] ?? '?'}
        </button>
        <span>{summary}</span>
        <Menu
          anchor={menuAnchor}
          open={menuAnchor !== null}
          onClose={() => setMenuAnchor(null)}
          label={`${label} type`}
          items={PRICE_TYPES.map((name, i) => ({ key: name, label: name, icon: priceIcon(i), onSelect: () => switchTo(i) }))}
        />
      </span>
    );
  }
  const set = (field: string, v: Json) => {
    if (onCommitAt) onCommitAt(field, v);
    else onCommit({ ...price, [field]: v });
  };
  return (
    <div className="price-field sentence" data-testid={`price-${p.name}`}>
      {disabled ? (
        <span className="card-chip price-type" aria-label={`${label} type: ${PRICE_TYPES[type] ?? '?'}`}>
          {priceIcon(type)} {PRICE_TYPES[type] ?? '?'}
        </span>
      ) : (
        <span className="price-type-control">
          <Tabs segmented ariaLabel={`${label} type`} value={String(type)} onChange={(k) => switchTo(Number(k))} items={PRICE_TYPES.map((name, i) => ({ key: String(i), label: <>{priceIcon(i)} {name}</> }))} />
        </span>
      )}
      {type === 0 && productParam && <FieldWidget param={{ ...productParam, displayName: `${label} product` }} value={price.product} compact disabled={disabled} owner={owner} onCommit={(v) => set('product', v)} />}
      {type === 0 && <Checkbox label="virtual" checked={Boolean(price.isVirtual)} disabled={disabled} onChange={(e) => set('isVirtual', e.target.checked)} />}
      {type === 1 && <ItemAmountList label={`${label} items`} value={price.items} disabled={disabled} owner={owner} onCommit={(items) => set('items', items)} onCommitAt={onCommitAt ? (rel, v) => onCommitAt(joinPath('items', rel), v) : undefined} />}
      {type === 2 && (
        <>
          <span>watch</span>
          <NumberInput size="s" kind="int" value={typeof price.ads === 'number' ? price.ads : 0} disabled={disabled} aria-label={`${label} ads`} onCommit={(v) => set('ads', Number(v ?? 0))} />
          <span>ad(s)</span>
        </>
      )}
    </div>
  );
}

/** `LiveOps.RewardDrop` list: item × count, weight, and the chance `weight / Σ positive weights`. */
export function RewardDropsField({ param: p, value, onCommit, onCommitAt, disabled, owner, compact }: WidgetProps) {
  const catalog = useContent();
  const itemParam = catalog.templates.get(ITEM_AMOUNT_TEMPLATE)?.effectiveParameters.find((x) => x.name === 'item');
  const rows = useMemo(() => (Array.isArray(value) ? (value as Json[]) : []), [value]);
  const total = rows.reduce<number>((sum, r) => sum + Math.max(0, Number(obj(r).weight ?? 0)), 0);
  if (compact) return <span className="muted small">{rows.length} drop{rows.length === 1 ? '' : 's'}</span>;
  const set = (i: number, rel: string, v: Json) => {
    if (onCommitAt) onCommitAt(`[${i}].${rel}`, v);
    else {
      const next = rows.map((r, j) => {
        if (j !== i) return r;
        const row = obj(r);
        if (rel.startsWith('item.')) return { ...row, item: { ...obj(row.item), [rel.slice(5)]: v } };
        return { ...row, [rel]: v };
      });
      onCommit(next);
    }
  };
  return (
    <div className="drops-field" data-testid={`drops-${p.name}`}>
      {rows.map((r, i) => {
        const row = obj(r);
        const item = obj(row.item);
        const weight = Math.max(0, Number(row.weight ?? 0));
        const chance = total > 0 && weight > 0 ? `${((weight / total) * 100).toFixed(1)}%` : weight === 0 ? 'never' : '—';
        return (
          <div key={i} className="row drop-row">
            {itemParam && <FieldWidget param={{ ...itemParam, displayName: `Drop ${i + 1} item` }} value={item.item} compact disabled={disabled} owner={owner} onCommit={(v) => set(i, 'item.item', v)} />}
            <span className="muted" aria-hidden="true">×</span>
            <NumberInput size="s" kind="int" value={typeof item.count === 'number' ? item.count : 1} disabled={disabled} aria-label={`Drop ${i + 1} count`} onCommit={(v) => set(i, 'item.count', Number(v ?? 1))} />
            <span className="muted small">weight</span>
            <NumberInput size="s" kind="int" value={typeof row.weight === 'number' ? row.weight : 1} disabled={disabled} aria-label={`Drop ${i + 1} weight`} onCommit={(v) => set(i, 'weight', Number(v ?? 0))} />
            <span className={`chip drop-chance ${chance === 'never' ? '' : 'chip-info'}`} title="weight divided by the sum of positive weights">{chance}</span>
            {!disabled && (
              <IconButton label={`Remove drop ${i + 1}`} onClick={() => onCommit(rows.filter((_, j) => j !== i))}>
                <X aria-hidden="true" />
              </IconButton>
            )}
          </div>
        );
      })}
      {rows.length === 0 && <span className="muted small">No drops yet.</span>}
      {!disabled && (
        <Button size="s" onClick={() => onCommit([...rows, { item: { item: null, count: 1 }, weight: 1 }])} icon={<Plus aria-hidden="true" />}>
          Add drop
        </Button>
      )}
    </div>
  );
}

export function isEconomyObject(p: Parameter): 'price' | 'reward' | null {
  if (p.type !== 'object') return null;
  if (p.template === PRICE_TEMPLATE) return 'price';
  if (p.template === REWARD_TEMPLATE) return 'reward';
  return null;
}
